import { useTranslation } from "react-i18next";
import { Navigate } from "react-router-dom";
import { Clock } from "lucide-react";
import { AppLayout } from "@/components/AppLayout";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/contexts/AuthContext";
import { ApprovalsPanel } from "@/components/timekeeping/ApprovalsPanel";
import { ReportsPanel } from "@/components/timekeeping/ReportsPanel";
import { TaskManager } from "@/components/timekeeping/TaskManager";
import { TimekeepingSettingsPanel } from "@/components/timekeeping/TimekeepingSettingsPanel";
import { AdminTimecardEntry } from "@/components/timekeeping/AdminTimecardEntry";

const tabTrigger =
  "rounded-none border-b-2 border-transparent px-1 pb-2 data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none";

export default function TimekeepingAdmin() {
  const { t } = useTranslation();
  const { user } = useAuth();

  const isAdminOrHr = user?.role.mainRole === "admin" || user?.role.mainRole === "hr";
  const isLineManager = !!(user?.isLineManager || user?.role.subRole === "line_manager");
  const canReview = isAdminOrHr || isLineManager;

  // Line managers only get Approvals; everything else is HR/admin.
  if (!canReview) return <Navigate to="/timekeeping" replace />;

  return (
    <AppLayout>
      <div className="space-y-6 w-full">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-xl bg-indigo-500/10 flex items-center justify-center">
            <Clock className="h-5 w-5 text-indigo-500" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">{t("timekeeping.admin.title", { defaultValue: "Timekeeping Admin" })}</h1>
            <p className="text-sm text-muted-foreground">
              {t("timekeeping.admin.subtitle", { defaultValue: "Approve timesheets, manage tasks, run reports and configure timekeeping." })}
            </p>
          </div>
        </div>

        <Tabs defaultValue="approvals">
          <TabsList className="bg-transparent h-auto p-0 border-b w-full justify-start rounded-none gap-4">
            <TabsTrigger value="approvals" className={tabTrigger}>
              {t("timekeeping.tabs.approvals", { defaultValue: "Approvals" })}
            </TabsTrigger>
            <TabsTrigger value="entry" className={tabTrigger}>
              {t("timekeeping.tabs.entry", { defaultValue: "Enter time" })}
            </TabsTrigger>
            {isAdminOrHr && (
              <TabsTrigger value="reports" className={tabTrigger}>
                {t("timekeeping.tabs.reports", { defaultValue: "Reports" })}
              </TabsTrigger>
            )}
            {isAdminOrHr && (
              <TabsTrigger value="tasks" className={tabTrigger}>
                {t("timekeeping.tabs.tasks", { defaultValue: "Tasks" })}
              </TabsTrigger>
            )}
            {isAdminOrHr && (
              <TabsTrigger value="settings" className={tabTrigger}>
                {t("timekeeping.tabs.settings", { defaultValue: "Settings" })}
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="approvals" className="pt-5"><ApprovalsPanel /></TabsContent>
          <TabsContent value="entry" className="pt-5"><AdminTimecardEntry /></TabsContent>
          {isAdminOrHr && <TabsContent value="reports" className="pt-5"><ReportsPanel /></TabsContent>}
          {isAdminOrHr && <TabsContent value="tasks" className="pt-5"><TaskManager /></TabsContent>}
          {isAdminOrHr && <TabsContent value="settings" className="pt-5"><TimekeepingSettingsPanel /></TabsContent>}
        </Tabs>
      </div>
    </AppLayout>
  );
}
